import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingCart, Users, Menu, X, Bell, LogOut, ChevronRight, ChevronDown, Boxes, Truck, FileCheck, TestTube2, Tag, Wallet, FileText, Star, Settings, UserCircle, Send, BarChart3 } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_9d18eba3-f81c-4175-a241-09119da09ef9/artifacts/lx9rjtoy_WhatsApp%20Image%202026-03-01%20at%204.03.51%20PM.jpeg";

const NAV_SECTIONS = [
  { title: "MAIN", items: [
    { path: "/admin", label: "Dashboard", icon: LayoutDashboard },
    { path: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  ]},
  { title: "COMMERCE", items: [
    { path: "/admin/products", label: "Products", icon: Package },
    { path: "/admin/orders", label: "Orders", icon: ShoppingCart },
    { path: "/admin/inventory", label: "Inventory", icon: Boxes },
    { path: "/admin/shipments", label: "Shipments", icon: Truck },
  ]},
  { title: "PEOPLE", items: [
    { path: "/admin/users", label: "Users", icon: Users },
  ]},
  { title: "HEALTH", items: [
    { path: "/admin/prescriptions", label: "Prescriptions", icon: FileCheck },
    { path: "/admin/lab-tests", label: "Lab Tests", icon: TestTube2 },
  ]},
  { title: "MARKETING", items: [
    { path: "/admin/coupons", label: "Coupons & Offers", icon: Tag },
    { path: "/admin/notifications", label: "Notifications", icon: Send },
  ]},
  { title: "FINANCE", items: [
    { path: "/admin/wallet", label: "Wallet", icon: Wallet },
  ]},
  { title: "CONTENT", items: [
    { path: "/admin/content", label: "Content", icon: FileText },
    { path: "/admin/reviews", label: "Reviews", icon: Star },
  ]},
  { title: "SYSTEM", items: [
    { path: "/admin/settings", label: "Settings", icon: Settings },
    { path: "/admin/profile", label: "My Profile", icon: UserCircle },
  ]},
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="flex h-screen bg-sky-50" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className={`${collapsed ? 'w-16' : 'w-60'} bg-white border-r border-slate-100 flex flex-col transition-all duration-300 shrink-0`}>
        <div className="p-4 border-b border-slate-100 flex items-center gap-2">
          <img src={LOGO_URL} alt="Logo" className="w-8 h-8 rounded-lg shrink-0" />
          {!collapsed && <span className="font-bold text-sm text-slate-800">Pharma <span className="text-cyan-600">Palace</span></span>}
        </div>
        <nav className="flex-1 p-2 space-y-4 overflow-y-auto">
          {NAV_SECTIONS.map(section => (
            <div key={section.title}>
              {!collapsed && <p className="px-3 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">{section.title}</p>}
              <div className="space-y-0.5">
                {section.items.map(item => (
                  <Link key={item.path} to={item.path} className={`flex items-center gap-3 px-3 py-2 rounded-xl text-[13px] transition-all ${location.pathname === item.path ? 'bg-cyan-50 text-cyan-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`} data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                    <item.icon className="w-4 h-4 shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100">
          <Link to="/" className={`flex items-center gap-2 px-3 py-2 text-xs text-slate-500 hover:text-cyan-600 rounded-xl hover:bg-slate-50`}>
            <ChevronRight className="w-3 h-3" /> {!collapsed && "Back to Store"}
          </Link>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-slate-100 px-6 py-3 flex items-center justify-between shrink-0">
          <button onClick={() => setCollapsed(!collapsed)} className="p-2 hover:bg-slate-50 rounded-xl" data-testid="admin-sidebar-toggle">
            {collapsed ? <Menu className="w-4 h-4" /> : <X className="w-4 h-4" />}
          </button>
          <div className="flex items-center gap-3">
            <button className="relative p-2 text-slate-500 hover:text-cyan-600"><Bell className="w-4 h-4" /></button>
            <div className="flex items-center gap-2">
              {user?.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-600 text-sm font-bold">{user?.name?.[0]}</div>}
              <div className="hidden sm:block"><p className="text-sm font-medium text-slate-800">{user?.name}</p><p className="text-[10px] text-slate-500">Admin</p></div>
            </div>
            <Button onClick={() => { logout(); navigate("/"); }} size="sm" variant="ghost" className="text-red-500 hover:text-red-600" data-testid="admin-logout-btn"><LogOut className="w-4 h-4" /></Button>
          </div>
        </header>
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
